import styled from '@emotion/styled';
import { css } from '@emotion/react';
import type { ThemeType } from 'radiance-ui/lib/constants';
import { MEDIA_QUERIES, SPACER } from 'radiance-ui/lib/constants';
import { TYPOGRAPHY_STYLE } from 'radiance-ui';

import { LOCAL_COLORS } from 'styles/constants';
import { mobileOnly } from 'styles/mixins/displayMobileDesktop';

export const InnerBox = styled.div`
  margin: auto;
  padding: ${SPACER.large};

  ${MEDIA_QUERIES.mdUp} {
    padding: ${SPACER.x3large};
    position: relative;
  }
`;

export const TopLinksbox = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  position: relative;
  ${MEDIA_QUERIES.mdUp} {
    flex-flow: row nowrap;
  }
`;

export const logoLink = () => css`
  width: 28px;
  ${MEDIA_QUERIES.mdUp} {
    width: 49px;
  }
`;

export const LinksFlexbox = styled.div`
  margin-top: ${SPACER.x2large};
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  position: relative;

  ${MEDIA_QUERIES.mdUp} {
    margin-top: 0;
  }
`;

export const MainLinksColumn = styled.div`
  margin-right: ${SPACER.x4large};

  ${MEDIA_QUERIES.mdUp} {
    margin-right: 90px;
  }
`;

const linkStyles = (theme: ThemeType) => css`
  ${TYPOGRAPHY_STYLE.body(theme)};
  margin-bottom: ${SPACER.small};
  display: block;
  font-weight: ${theme.TYPOGRAPHY.fontWeight.bold};

  &:hover {
    text-decoration: solid underline ${LOCAL_COLORS.surfaceOrange} 4px;
    text-decoration-skip-ink: none;
  }
`;

export const mainLink = (theme: ThemeType, isMuted?: boolean) => css`
  ${linkStyles(theme)};
  color: ${isMuted ? theme.COLORS.primaryTint3 : theme.COLORS.white};
`;

const footerLinkStyle = () => css`
  text-decoration: underline;
  font-size: 12px;
  color: ${LOCAL_COLORS.surfaceWhite};
  margin-right: ${SPACER.medium};
`;

export const legalLink = () => css`
  ${footerLinkStyle()};
  background-color: transparent;
  border: 0;
  cursor: pointer;
  text-align: left;
  text-decoration: none;

  &:hover {
    text-decoration: none;
    opacity: 0.8;
  }
  ${MEDIA_QUERIES.mdUp} {
    margin-right: ${SPACER.large};
    padding: 0;
  }
`;

export const CopyrightText = styled.div`
  font-size: 12px;
  color: ${LOCAL_COLORS.surfaceWhite};
  width: 100%;

  ${MEDIA_QUERIES.mdUp} {
    margin-top: 0;
    width: initial;
  }
`;

export const RightImageWrapper = styled.div<{ backgroundColor?: string }>`
  background: ${({ theme, backgroundColor }) =>
    backgroundColor || theme.COLORS.primary};
  cursor: pointer;

  ${MEDIA_QUERIES.mdUp} {
    top: 0;
    bottom: initial;
    position: absolute;
    bottom: 5rem;
    right: 0;
  }
`;

export const rightImageStyles = css`
  display: inline-block;
  width: ${SPACER.x5large};

  ${MEDIA_QUERIES.mdUp} {
    margin-left: ${SPACER.medium};
  }
`;

export const BottomBox = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  align-items: center;

  br {
    ${mobileOnly()};
  }
`;

export const LegalLinks = styled.div`
  display: flex;
  flex-flow: row wrap;
  gap: ${SPACER.medium};
  justify-content: flex-start;
  align-items: flex-start;
  margin-top: ${SPACER.large};

  ${MEDIA_QUERIES.mdUp} {
    margin-top: ${SPACER.x2large};
  }

  & > div {
    display: flex;
    flex-direction: column;
  }
`;

export const SocialLinksBox = styled.div`
  flex: 1 1 100%;
  padding: ${SPACER.xlarge} 0;

  ${MEDIA_QUERIES.mdUp} {
    display: block;
    padding: 0 108px;
    border: none;
    flex: 1 1 50%;
  }
`;

export const MainBox = styled.footer<{ backgroundColor?: string }>`
  background: ${({ backgroundColor }) => backgroundColor};
  position: relative;
  flex: 1;
`;

export const EfficacyBox = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: ${SPACER.medium};

  ${MEDIA_QUERIES.mdUp} {
    flex-direction: row;
  }
`;

export const DisclaimerBox = styled.div`
  ${footerLinkStyle()};
  text-decoration: none;
  display: flex;
  flex-direction: column;

  a {
    ${footerLinkStyle()};
    text-decoration: underline;
  }

  sup {
    font-size: 8px;
  }

  ${MEDIA_QUERIES.mdUp} {
    div {
      margin-right: ${SPACER.medium};
    }
  }
`;
